'use client';

import { useCallback, useEffect, useState } from 'react';
import { Check, X, RefreshCw, Loader2, ShieldAlert, CheckCircle2 } from 'lucide-react';

// THE SEED GATE — Boss approves before a cent is spent.
//
// "sometimes you fuck up with the seeds really badly... I want to approve those
// seeds. It should be like 20 to 40 seeds."
//
// Every seed shows WHERE IT CAME FROM, so this is approving evidence rather than
// a list of words. Default is undecided: a seed is only researched if Boss
// actively keeps it. Silence never means yes.

type Seed = { seed: string; source: string; reason: string; approved: boolean | null };

export function SeedGate({ slug, path }: { slug: string; path: string }) {
  const [seeds, setSeeds] = useState<Seed[]>([]);
  const [meta, setMeta] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState<string | null>(null);
  const [configWritten, setConfigWritten] = useState(false);

  const load = useCallback(async () => {
    const d = await fetch(`/api/pipeline/seeds?path=${encodeURIComponent(path)}`)
      .then(r => r.json()).catch(() => null);
    if (!d?.ok) return;
    setSeeds(d.proposed?.seeds || []);
    setMeta(d.proposed || null);
    setConfigWritten(!!d.configWritten);
  }, [path]);

  useEffect(() => { load(); }, [load]);

  const call = async (action: string, extra: any = {}) => {
    setBusy(true); setOut(null);
    const r = await fetch('/api/pipeline/seeds', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path, action, ...extra }),
    }).then(x => x.json()).catch(() => ({ ok: false, error: 'request failed' }));
    setBusy(false);
    setOut(r.out || r.error || null);
    load();
  };

  const set = (i: number, v: boolean | null) =>
    setSeeds(s => s.map((x, n) => (n === i ? { ...x, approved: x.approved === v ? null : v } : x)));

  const kept = seeds.filter(s => s.approved === true).length;
  const cut = seeds.filter(s => s.approved === false).length;
  const undecided = seeds.length - kept - cut;
  const inRange = kept >= 20 && kept <= 40;

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-xl bg-amber-500/10 ring-1 ring-amber-500/25 px-3 py-2.5
                      flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
        <div className="text-[12px] text-amber-200/90 leading-relaxed">
          <b>Boss gate — before any paid API call.</b> A bad seed set doesn&apos;t fail loudly;
          it returns clean data and builds a confident, complete, wrong site.
          Nothing runs until you approve.
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => call('propose')} disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                     bg-white/5 text-gray-300 hover:bg-white/10 disabled:opacity-40">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
          {seeds.length ? 'Re-propose from brief' : 'Propose seeds from brief'}
        </button>
        {/* Bulk keep/cut. Without these, 335 proposed seeds = 335 taps on a
            phone — Boss: "there is no way to approve" (2026-09-13). */}
        {seeds.length > 0 && (
          <>
            <button onClick={() => setSeeds(s => s.map(x => ({ ...x, approved: true })))} disabled={busy}
              className="px-2.5 py-1.5 rounded-lg text-xs bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40">
              Keep all
            </button>
            <button onClick={() => setSeeds(s => s.map(x => ({ ...x, approved: null })))} disabled={busy}
              className="px-2.5 py-1.5 rounded-lg text-xs bg-white/5 text-gray-400 hover:bg-white/10 disabled:opacity-40">
              Reset
            </button>
            {/* REJECT ALL — Boss, 2026-09-19. The seed set is wrong as a whole,
                usually because the brief gave placeholder headings instead of
                real terms. Cutting 22 boxes one at a time is not the answer.
                Records the reason, drops the proposal, parks the run. */}
            <button
              onClick={() => call('reject', { reason: 'all seeds rejected' })}
              disabled={busy}
              title="One click, no questions — Beast diagnoses, fixes the brief, re-proposes"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs bg-red-500/10 text-red-300 hover:bg-red-500/25 disabled:opacity-40">
              <X className="h-3.5 w-3.5" /> Reject all
            </button>
          </>
        )}
        <div className="flex-1" />
        {seeds.length > 0 && (
          <span className="text-[11px] text-gray-500">
            <span className="text-emerald-400">{kept} keep</span> ·{' '}
            <span className="text-red-400">{cut} cut</span> ·{' '}
            <span className="text-gray-500">{undecided} undecided</span> · {seeds.length} proposed
          </span>
        )}
        <button
          onClick={() => call('approve', { seeds })}
          disabled={busy || kept === 0}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs disabled:opacity-30 ${
            inRange ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30'
                    : 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25'}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Approve {kept} seed{kept === 1 ? '' : 's'}
        </button>
      </div>

      {seeds.length > 0 && !inRange && kept > 0 && (
        <div className="text-[11px] text-amber-300/80">
          {kept < 20
            ? `${kept} is below the 20 target — thin seed sets miss real demand.`
            : `${kept} is above the 40 target — every extra seed costs money at the next stage.`}
          {' '}You can still approve; this is guidance, not a block.
        </div>
      )}

      {configWritten && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10
                        ring-1 ring-emerald-500/25 px-3 py-2 text-[12px] text-emerald-300">
          <CheckCircle2 className="h-4 w-4" />
          research-config.json written — Stage 0 research can start.
        </div>
      )}

      {out && (
        <pre className="whitespace-pre-wrap break-words bg-black/40 rounded-lg p-2.5
                        text-[11px] text-gray-300 max-h-40 overflow-y-auto">{out}</pre>
      )}

      {seeds.length === 0 ? (
        <div className="rounded-xl bg-black/30 ring-1 ring-white/10 p-4 text-xs text-gray-500">
          No seeds proposed yet. They are derived from the Brief — finish the niche,
          expansions and geography there first, then propose.
        </div>
      ) : (
        <div className="rounded-xl bg-black/30 ring-1 ring-white/10 overflow-hidden">
          {meta?.derived_from && (
            <div className="px-3 py-2 border-b border-white/5 text-[11px] text-gray-500">
              from brief · terms: <span className="text-gray-400">{(meta.derived_from.terms || []).join(', ')}</span>
              {(meta.derived_from.excludes || []).length > 0 && <>
                {' '}· excluding <span className="text-red-400/80">{meta.derived_from.excludes.join(', ')}</span></>}
            </div>
          )}
          <div className="max-h-[520px] overflow-y-auto divide-y divide-white/5">
            {seeds.map((s, i) => (
              <div key={s.seed} className={`flex items-center gap-2 px-3 py-1.5 ${
                s.approved === true ? 'bg-emerald-500/5'
                : s.approved === false ? 'bg-red-500/5 opacity-50' : ''}`}>
                <button onClick={() => set(i, true)}
                  className={`h-5 w-5 rounded flex items-center justify-center shrink-0 ${
                    s.approved === true ? 'bg-emerald-500/25 text-emerald-300'
                                        : 'bg-white/5 text-gray-600 hover:text-emerald-300'}`}
                  title="keep">
                  <Check className="h-3 w-3" />
                </button>
                <button onClick={() => set(i, false)}
                  className={`h-5 w-5 rounded flex items-center justify-center shrink-0 ${
                    s.approved === false ? 'bg-red-500/25 text-red-300'
                                         : 'bg-white/5 text-gray-600 hover:text-red-300'}`}
                  title="cut">
                  <X className="h-3 w-3" />
                </button>
                <span className={`text-xs flex-1 ${
                  s.approved === false ? 'line-through text-gray-600' : 'text-gray-200'}`}>
                  {s.seed}
                </span>
                <span className="text-[10px] text-gray-600 hidden sm:block truncate max-w-[46%]"
                      title={s.reason}>{s.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
